import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <SEO
        title="About - Koby Pierce"
        description="Learn about Koby Pierce, young entrepreneur and technology innovator behind Hybrid Hosting, UpPacket, and BarnBills. Passionate about AI, space exploration, and building a more peaceful world through technology."
        keywords="About Koby Pierce, Young Entrepreneur, Technology Innovation, Hybrid Hosting, UpPacket, BarnBills, AI, Space Exploration, Peace"
        url="https://kobypierce.com/about"
      />
      <Header />

      {/* Hero Section */}
      <div className="w-full relative pt-24 pb-16">
        {/* Background with gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/30 via-purple-900/20 to-[#0d0d0d]"></div>
        {/* Top gradient overlay */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0d0d0d] to-transparent"></div>
        <div className="relative z-10">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-4">About Me</h1>
            <p className="text-xl text-gray-300">
              Young entrepreneur building the future, one innovation at a time
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full bg-[#0d0d0d] min-h-screen">
        <div className="max-w-6xl mx-auto px-4 py-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">Hello, I'm Koby Pierce</h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  I'm a young entrepreneur and technology enthusiast with a passion for building innovative solutions 
                  that solve real-world problems. My journey in technology spans from web development and hosting 
                  infrastructure to cutting-edge AI applications and space exploration.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  I'm the CEO of Hybrid Hosting, a cloud infrastructure company, and I'm also building UpPacket 
                  and BarnBills. When I'm not working on my companies, you'll find me tinkering with RC cars, 
                  exploring AI technologies, or researching space technology and autonomous systems as hobbies.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  My ultimate vision is creating a world of peace through sustainable abundance and exploring the stars 
                  that have always fascinated me. I believe technology can push humanity forward, creating products 
                  that people truly love while building a more peaceful Earth and reaching for the cosmos.
                </p>
              </div>
              <div className="flex justify-center">
                <div className="w-80 h-80 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                  <img 
                    src="/vendor/pfp.png" 
                    alt="Koby Pierce" 
                    className="w-72 h-72 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-72 h-72 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 items-center justify-center text-6xl font-bold hidden">
                    KP
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Philosophy */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-8 rounded-lg border border-blue-700/30">
              <h2 className="text-3xl font-bold text-center mb-6">My Philosophy</h2>
              <div className="max-w-4xl mx-auto text-center">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  "Technology should solve real problems and make life better for everyone. 
                  I believe in building solutions that are not just innovative, but practical 
                  and accessible to those who need them most."
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  I believe humans were built to love each other, not hate. My vision is a world where sustainable 
                  abundance creates peace, where we explore the stars together, and where technology serves humanity's 
                  highest aspirations. Through authentic work, whether that's providing reliable hosting infrastructure, 
                  building tools for industries like BarnBills, or sharing genuine thoughts on this blog, I want to 
                  contribute to pushing our species forward toward a more peaceful Earth and the infinite cosmos beyond.
                </p>
              </div>
            </div>
          </div>

          {/* What You'll Find Here */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-8 rounded-lg border border-purple-700/30">
              <h2 className="text-3xl font-bold text-center mb-6">What You'll Find Here</h2>
              <div className="max-w-4xl mx-auto">
                <p className="text-gray-300 text-lg leading-relaxed mb-6 text-center">
                  This is my digital sanctuary, a place where I can share my authentic self, 
                  my work, my opinions, and things I find interesting on the open web.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#4d4d4d]">
                    <h3 className="text-xl font-bold mb-3 text-blue-400">Company Updates</h3>
                    <p className="text-gray-300">
                      Behind the scenes looks at building Hybrid Hosting, UpPacket, and BarnBills. 
                      Real stories about the challenges and victories of young entrepreneurship.
                    </p>
                  </div>
                  <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#4d4d4d]">
                    <h3 className="text-xl font-bold mb-3 text-green-400">Tech Deep Dives</h3>
                    <p className="text-gray-300">
                      Exploring AI technologies, space tech research, product reviews, 
                      and honest insights from a tech enthusiast's perspective.
                    </p>
                  </div>
                  <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#4d4d4d]">
                    <h3 className="text-xl font-bold mb-3 text-purple-400">Personal Projects</h3>
                    <p className="text-gray-300">
                      Updates on my RC car projects, hobby explorations, and the intersection 
                      of technology with everyday interests and passions.
                    </p>
                  </div>
                  <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#4d4d4d]">
                    <h3 className="text-xl font-bold mb-3 text-orange-400">Industry Insights</h3>
                    <p className="text-gray-300">
                      Thoughtful discussions about technology regulation, industry trends, 
                      and the future of innovation, without the political arguments.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">Let's Connect</h2>
            <p className="text-gray-300 text-lg mb-8">
              Whether you're a potential client, someone curious about who I really am, 
              or just interested in technology and entrepreneurship, I'd love to hear from you.
            </p>
            <a 
              href="/contact" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition duration-200"
            >
              Get In Touch
            </a>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto">
        <Footer />
      </div>
    </div>
  );
};

export default AboutPage; 